import { Button, Dialog, DialogContent } from "@mui/material";
import * as Sentry from "@sentry/react";
import { useQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  type ErrorComponentProps,
  Navigate,
  Outlet,
  useMatchRoute,
  useRouter,
} from "@tanstack/react-router";
import clsx from "clsx";
import { useMemo } from "react";
import { useTranslations } from "use-intl";

import { BookingError } from "@/components/Errors/BookingError";
import { ErrorPanel } from "@/components/Errors/ErrorPanel";
import { OfferNotAvailableError } from "@/components/Errors/OfferNotAvailableError";
import { IconSadFace } from "@/components/Icons";
import { WhatsAppButton } from "@/components/WhatsApp/WhatsAppButton";
import { usePurchaseStateContext } from "@/contexts/PurchaseStateContext";
import { useFlightSearchDataContext } from "@/features/flights/contexts/FlightSearchDataContext";
import { useFlightSelectionPayloadContext } from "@/features/flights/contexts/FlightSelectionPayloadContext/FlightSelectionPayloadContext";
import { flightAvailabilityQueryOptions } from "@/features/flights/hooks/useFlightAvailability";
import { TravellersSelectionProvider } from "@/features/travellers/contexts/TravellersSelectionContext";
import { travellersQueryOptions } from "@/features/travellers/hooks/useTravellers";
import { useRouteConfig } from "@/hooks/useRouteConfig";
import { type OfferSelectedEventData } from "@/types/types";
import { isOfferIsNotAvailableAnymoreError } from "@/utils/assertions";

export const Route = createFileRoute("/_authenticated/_flights/_flight-offer")({
  component: RouteComponent,
  errorComponent: (props: ErrorComponentProps) => {
    Sentry.captureException(props.error);

    if (isOfferIsNotAvailableAnymoreError(props.error)) {
      return <OfferNotAvailableError variant="flight" />;
    }

    return <BookingError {...props} type="flights" />;
  },
  onEnter: () => {
    const event = new CustomEvent<OfferSelectedEventData>("offerselected", {
      detail: {
        offerType: "air",
      },
    });
    window.dispatchEvent(event);
  },
});

function RouteComponent() {
  const { history } = useRouter();
  const routeConfig = useRouteConfig();
  const t = useTranslations();

  const { selectedFlightPayload } = useFlightSelectionPayloadContext();

  const { searchData } = useFlightSearchDataContext();
  const { purchaseState } = usePurchaseStateContext();
  useQuery(travellersQueryOptions()); // for preloading
  const { error } = useQuery(
    flightAvailabilityQueryOptions(selectedFlightPayload),
  );

  const matches = useMatchRoute();

  const isTicketPurchasedRoute = matches({
    to: "/flights/offer/$id/step/final",
    from: "/",
  });

  const initialTravellersSlots = useMemo(() => {
    if (!searchData) return;

    return {
      ADT: Array.from({ length: searchData.adult }, () => null),
      CHD: Array.from({ length: searchData.child }, () => null),
      INF: Array.from({ length: searchData.infantWithoutSeat }, () => null),
    };
  }, [searchData]);

  if (!isTicketPurchasedRoute && purchaseState === "completed") {
    return <Navigate to="/" />;
  }

  return (
    <TravellersSelectionProvider
      initialTravellersSlots={initialTravellersSlots}
      travelDate={new Date(searchData!.dates.departure)}
    >
      <main
        className={clsx("no-footer main", {
          "with-drawer": !!routeConfig.drawer,
        })}
      >
        <div className="mt-2 mb-4 min-h-full w-full">
          <Outlet />

          <Dialog
            open={isOfferIsNotAvailableAnymoreError(error)}
            fullWidth
            maxWidth="xs"
          >
            <DialogContent>
              <ErrorPanel
                icon={IconSadFace}
                primaryText={t("flights.notAvailable.title")}
                secondaryText={t("flights.notAvailable.description")}
              >
                <Button
                  variant="outlined"
                  color="primary"
                  size="small"
                  onClick={() => {
                    history.back();
                  }}
                  sx={{ borderRadius: "var(--radius-2xl)" }}
                >
                  {t("flights.notAvailable.button")}
                </Button>
              </ErrorPanel>
            </DialogContent>
          </Dialog>

          {!isTicketPurchasedRoute && <WhatsAppButton bottomPosition="20vh" />}
        </div>
      </main>
    </TravellersSelectionProvider>
  );
}
